import config from "@/modules/config";
import { constants } from "@/modules/Constants";
import {
  pauseOverlayPlayback,
  resumeOverlayPlayback,
} from "@/modules/overlayPlayback";
import type { LegacyMessage } from "@/types/messages";

let installed = false;
const stash: Record<string, unknown> = {};
const waiters: Record<string, (body: unknown) => void> = {};

/**
 * When pix-pt / pt names embedding page URL, require message origin to match it.
 * Skip check when parent URL unknown or non-http (e.g. srcdoc wildcard flows).
 */
function prefetchMessageOriginOk(eventOrigin: string): boolean {
  const pt = config.targetOrigin();
  if (!pt || pt === "*") {
    return true;
  }
  if (!/^https?:\/\//i.test(pt)) {
    return true;
  }
  try {
    return new URL(pt).origin === eventOrigin;
  } catch {
    return false;
  }
}

function parsePrefixedMessage(event: MessageEvent): LegacyMessage | null {
  const prefix = constants.MESSAGE_PREFIX;
  if (typeof event.data !== "string" || !event.data.startsWith(prefix)) {
    return null;
  }
  try {
    return JSON.parse(event.data.substring(prefix.length));
  } catch {
    return null;
  }
}

function deliver(expIdStr: string, body: unknown) {
  if (waiters[expIdStr]) {
    waiters[expIdStr](body);
    delete waiters[expIdStr];
    return;
  }
  stash[expIdStr] = body;
}

/**
 * Listen for parent postMessage experience_prefetch (overlay embed).
 */
export function installPrefetchListener() {
  if (installed) {
    return;
  }
  installed = true;

  window.addEventListener(
    "message",
    function (event) {
      if (!window.parent || event.source !== window.parent) {
        return;
      }
      if (!prefetchMessageOriginOk(event.origin)) {
        return;
      }
      const data = parsePrefixedMessage(event);
      if (!data || !data.type) {
        return;
      }
      if (data.type === "overlay_pause") {
        pauseOverlayPlayback();
        return;
      }
      if (data.type === "overlay_play") {
        resumeOverlayPlayback();
        return;
      }
      if (data.type !== "experience_prefetch") {
        return;
      }
      const expId = data.expId != null ? String(data.expId) : "";
      if (!expId || data.body === undefined) {
        return;
      }
      deliver(expId, data.body);
    },
    false,
  );
}

/**
 * Resolve with prefetched API body, or null after timeout (then fetch it).
 */
export function waitForParentPrefetch(
  expId: unknown,
  ms: number,
): Promise<unknown> {
  const expIdStr = String(expId);
  return new Promise(function (resolve) {
    if (stash[expIdStr] !== undefined) {
      const cached = stash[expIdStr];
      delete stash[expIdStr];
      resolve(cached);
      return;
    }
    const timer = setTimeout(function () {
      if (waiters[expIdStr]) {
        delete waiters[expIdStr];
      }
      resolve(null);
    }, ms);

    waiters[expIdStr] = function (body) {
      clearTimeout(timer);
      resolve(body);
    };
  });
}
